import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, MapPin, Camera, Save, Building2, KeyRound, Lock, Eye, EyeOff } from 'lucide-react';
import ThemeColorsCard from '../components/ThemeColorsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClienteProfile() {
  const navigate = useNavigate();
  const { user, updateUser, loading: authLoading } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: '', phone: '', endereco: '', profile_image: '', theme_colors: {} });
  const [client, setClient] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ c: false, n: false, cf: false });
  const [changingPw, setChangingPw] = useState(false);

  const handleChangePassword = async () => {
    if (!pw.current || !pw.next) { toast.error('Preencha as senhas'); return; }
    if (pw.next !== pw.confirm) { toast.error('Nova senha e confirmação não coincidem'); return; }
    if (pw.next.length < 6) { toast.error('Senha nova precisa ter no mínimo 6 caracteres'); return; }
    setChangingPw(true);
    try {
      await axios.post(`${API}/cliente/me/change-password`, {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success('Senha atualizada!');
      setPw({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao trocar senha');
    }
    setChangingPw(false);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'cliente') { navigate('/login'); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/cliente/me/profile`);
        setForm({
          name: res.data.name || '',
          phone: res.data.phone || '',
          endereco: res.data.client?.endereco || '',
          profile_image: res.data.profile_image || '',
          theme_colors: res.data.theme_colors || {},
        });
        setClient(res.data.client || {});
      } catch { toast.error('Erro ao carregar perfil'); }
      setLoading(false);
    })();
  }, [authLoading, user]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Imagem muito grande (máx 2MB)'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setForm(prev => ({ ...prev, profile_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cliente/me/profile`, form);
      if (updateUser) updateUser({ name: form.name, phone: form.phone, profileImage: form.profile_image });
      toast.success('Perfil atualizado!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/cliente/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="flex-1 font-bold text-gray-900 dark:text-white">Meu Perfil</h1>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5"
            data-testid="save-profile-btn"
          >
            {saving ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {/* Avatar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center gap-3">
          <div className="relative" data-testid="profile-avatar-wrapper">
            {form.profile_image ? (
              <img src={form.profile_image} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-emerald-500" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center border-4 border-emerald-500">
                <User className="w-10 h-10 text-emerald-600" />
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="absolute bottom-0 right-0 bg-emerald-600 hover:bg-emerald-700 text-white p-1.5 rounded-full shadow-lg"
              data-testid="change-avatar-btn"
            >
              <Camera className="w-3.5 h-3.5" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
          </div>
          <p className="text-xs text-gray-500">Clique no ícone da câmera para mudar a foto</p>
        </div>

        {/* Dados pessoais */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-3">
          <h2 className="font-bold text-sm text-gray-900 dark:text-white">Dados Pessoais</h2>
          <Field icon={User} label="Nome">
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="profile-name" />
          </Field>
          <Field icon={Mail} label="E-mail">
            <input type="email" value={user?.email || ''} disabled
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-900/40 text-gray-500" />
          </Field>
          <Field icon={Phone} label="Telefone">
            <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(11) 99999-9999"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="profile-phone" />
          </Field>
          <Field icon={MapPin} label="Endereço">
            <textarea value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })}
              rows={2} placeholder="Rua, número, bairro, cidade..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
              data-testid="profile-endereco" />
          </Field>
        </div>

        {/* Dados da empresa (somente leitura) */}
        {client?.nome && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-2">
            <h2 className="font-bold text-sm flex items-center gap-1.5 text-gray-900 dark:text-white"><Building2 className="w-4 h-4" /> Empresa</h2>
            <ReadOnly label="Razão Social" value={client.nome} />
            <ReadOnly label="CNPJ/CPF" value={client.cnpj_cpf} />
            <ReadOnly label="WhatsApp" value={client.whatsapp} />
            {client.cidade && <ReadOnly label="Cidade/UF" value={`${client.cidade}${client.estado ? '/' + client.estado : ''}`} />}
            <p className="text-[10px] text-gray-500 mt-2">Para alterar dados da empresa, fale com o admin.</p>
          </div>
        )}

        {/* Troca de senha */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 space-y-3" data-testid="cliente-change-password-card">
          <h2 className="font-bold text-sm flex items-center gap-2 text-gray-900 dark:text-white">
            <KeyRound className="w-4 h-4 text-amber-500" />
            Trocar Senha
          </h2>
          {[
            { key: 'current', label: 'Senha atual', showKey: 'c' },
            { key: 'next', label: 'Nova senha (mín. 6 caracteres)', showKey: 'n' },
            { key: 'confirm', label: 'Confirmar nova senha', showKey: 'cf' },
          ].map(({ key, label, showKey }) => (
            <div key={key}>
              <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">{label}</label>
              <div className="relative">
                <input
                  type={showPw[showKey] ? 'text' : 'password'}
                  value={pw[key]}
                  onChange={(e) => setPw({ ...pw, [key]: e.target.value })}
                  className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-emerald-500 outline-none"
                  data-testid={`cliente-pw-${key}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => ({ ...p, [showKey]: !p[showKey] }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={handleChangePassword}
            disabled={changingPw}
            className="w-full sm:w-auto px-4 py-2 text-sm font-semibold bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white rounded-lg flex items-center justify-center gap-2"
            data-testid="cliente-submit-change-password"
          >
            {changingPw ? 'Alterando...' : (<><Lock className="w-4 h-4" /> Trocar Senha</>)}
          </button>
        </div>

        {/* Personalização de cores */}
        <ThemeColorsCard
          endpoint="/cliente/me/theme-colors"
          initialColors={form.theme_colors}
          onSaved={(c) => setForm(f => ({ ...f, theme_colors: c }))}
          testidPrefix="cliente-theme"
        />
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5" /> {label}
    </label>
    {children}
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div className="flex justify-between text-sm gap-3">
    <span className="text-gray-600 dark:text-gray-300">{label}:</span>
    <span className="text-gray-900 dark:text-white font-medium truncate">{value || '-'}</span>
  </div>
);
